import React, { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex justify-center items-center min-h-screen bg-background-default dark:bg-background-dark px-6">
          <div className="flex flex-col items-center text-text-primary dark:text-text-primaryDark max-w-md text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mb-6" />
            <h2 className="text-2xl font-semibold mb-2">Something went wrong</h2>
            <p className="text-sm text-text-secondary dark:text-text-secondaryDark mb-6">
              We've been notified and are looking into it. Please try refreshing the page.
            </p>
            {this.state.error && (
              <details className="mb-6 w-full text-left">
                <summary className="text-xs text-text-secondary dark:text-text-secondaryDark cursor-pointer mb-2">
                  Error details
                </summary>
                <pre className="text-xs bg-background-panel dark:bg-background-panelDark p-3 rounded overflow-auto">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
            <button
              onClick={this.handleReset}
              className="px-6 py-3 bg-secondary text-white rounded-lg font-medium hover:bg-secondary-hover transition flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default Sentry.withErrorBoundary(ErrorBoundary, {
  fallback: ({ error }) => (
    <div className="flex justify-center items-center min-h-screen bg-background-default dark:bg-background-dark px-6">
      <div className="flex flex-col items-center text-text-primary dark:text-text-primaryDark max-w-md text-center">
        <AlertTriangle className="w-16 h-16 text-red-500 mb-6" />
        <h2 className="text-2xl font-semibold mb-2">Something went wrong</h2>
        <p className="text-sm text-text-secondary dark:text-text-secondaryDark mb-6">
          {error?.message || "An unexpected error occurred"}
        </p>
        <button
          onClick={() => window.location.href = "/"}
          className="px-6 py-3 bg-secondary text-white rounded-lg font-medium hover:bg-secondary-hover transition flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Go Home
        </button>
      </div>
    </div>
  ),
});

